/**
 * Print utility helpers for generating HTML-based printable documents.
 * Called from template builders rather than components, so `precision` is passed in.
 */
import {
  TOTAL_PRECISION,
  format,
  type DecimalPrecision,
} from "@/utils/decimal";

export const formatCurrency = (
  amount: number,
  currencyCode: string,
  precision: DecimalPrecision | number,
): string => {
  const formatter = new Intl.NumberFormat("en-MY", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });

  return formatter.format(amount);
};

/** Document totals always print at 2dp, whatever the company's line precision. */
export const formatTotalCurrency = (
  amount: number,
  currencyCode: string,
): string => formatCurrency(amount, currencyCode, TOTAL_PRECISION);

export const formatNumber = (
  value: number,
  decimals: DecimalPrecision | number,
): string => format(value, decimals);

export const formatDate = (date: Date | string): string => {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-MY", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

export const formatDateTime = (date: Date | string): string => {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleString("en-MY", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Converts a base64 string or URL to an image data URL
 */
export const getImageDataUrl = async (urlOrBase64: string): Promise<string> => {
  if (urlOrBase64.startsWith("data:")) {
    return urlOrBase64;
  }

  try {
    const response = await fetch(urlOrBase64);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error converting image:", error);
    return "";
  }
};

/**
 * Opens a print dialog with the provided HTML content
 */
export const printHtml = (htmlContent: string, documentTitle: string = "Document") => {
  // Create a new window for printing
  const printWindow = window.open("", "_blank");

  if (!printWindow) {
    alert("Please allow popups to print the document.");
    return;
  }

  // Write the HTML content
  printWindow.document.write(htmlContent);
  printWindow.document.close();

  // Wait for images and styles to load
  printWindow.onload = () => {
    printWindow.document.title = documentTitle;

    // Small delay to ensure everything is rendered
    setTimeout(() => {
      printWindow.print();

      // Optional: Close the window after printing
      // printWindow.onafterprint = () => {
      //   printWindow.close();
      // };
    }, 250);
  };
};

/**
 * Common CSS styles for all print documents
 */
export const getPrintStyles = () => `
  <style>
    @page {
      size: A4;
      margin: 15mm;
    }

    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      font-size: 10pt;
      line-height: 1.4;
      color: #333;
      background: white;
    }

    .container {
      max-width: 210mm;
      margin: 0 auto;
      padding: 10mm;
      background: white;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 20px;
      padding-bottom: 15px;
      border-bottom: 2px solid #333;
    }

    .company-info {
      flex: 1;
    }

    .company-logo {
      max-width: 150px;
      max-height: 80px;
      margin-bottom: 10px;
    }

    .company-name {
      font-size: 18pt;
      font-weight: bold;
      margin-bottom: 5px;
      color: #1a1a1a;
    }

    .company-details {
      font-size: 9pt;
      color: #555;
      line-height: 1.6;
    }

    .document-title {
      text-align: right;
      flex: 0 0 auto;
    }

    .document-type {
      font-size: 24pt;
      font-weight: bold;
      color: #2563eb;
      margin-bottom: 5px;
    }

    .document-number {
      font-size: 12pt;
      font-weight: 600;
      color: #333;
      margin-bottom: 3px;
    }

    .document-date {
      font-size: 9pt;
      color: #666;
    }

    .status-badge {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 4px;
      font-size: 9pt;
      font-weight: 600;
      margin-top: 5px;
    }

    .status-draft { background: #e5e7eb; color: #374151; }
    .status-sent { background: #dbeafe; color: #1e40af; }
    .status-confirmed { background: #dcfce7; color: #166534; }
    .status-processing { background: #fef3c7; color: #92400e; }
    .status-delivered { background: #d1fae5; color: #065f46; }
    .status-cancelled { background: #fee2e2; color: #991b1b; }

    .parties-section {
      display: flex;
      justify-content: space-between;
      margin: 20px 0;
      gap: 20px;
    }

    .party-box {
      flex: 1;
      padding: 12px;
      border: 1px solid #d1d5db;
      border-radius: 4px;
      background: #f9fafb;
    }

    .party-title {
      font-size: 11pt;
      font-weight: 700;
      margin-bottom: 8px;
      color: #1f2937;
      border-bottom: 1px solid #d1d5db;
      padding-bottom: 4px;
    }

    .party-details {
      font-size: 9pt;
      line-height: 1.6;
    }

    .party-details strong {
      display: inline-block;
      min-width: 80px;
      color: #4b5563;
    }

    .items-table {
      width: 100%;
      border-collapse: collapse;
      margin: 20px 0;
      font-size: 9pt;
    }

    .items-table th {
      background: #1f2937;
      color: white;
      padding: 8px 6px;
      text-align: left;
      font-weight: 600;
      border: 1px solid #374151;
    }

    .items-table td {
      padding: 6px;
      border: 1px solid #d1d5db;
      vertical-align: top;
      word-break: break-word;
      overflow-wrap: break-word;
    }

    .items-table tbody tr:nth-child(even) {
      background: #f9fafb;
    }

    .items-table .text-right {
      text-align: right;
    }

    .items-table .text-center {
      text-align: center;
    }

    .totals-section {
      margin: 20px 0;
      display: flex;
      justify-content: flex-end;
    }

    .totals-table {
      width: 300px;
      font-size: 10pt;
    }

    .totals-table tr td {
      padding: 5px 10px;
    }

    .totals-table tr td:first-child {
      text-align: right;
      font-weight: 600;
      color: #4b5563;
    }

    .totals-table tr td:last-child {
      text-align: right;
      font-weight: 500;
    }

    .totals-table tr.total-row {
      border-top: 2px solid #333;
      font-weight: 700;
      font-size: 12pt;
    }

    .totals-table tr.total-row td {
      padding-top: 10px;
      color: #1a1a1a;
    }

    .footer-section {
      margin-top: 30px;
      padding-top: 15px;
      border-top: 1px solid #d1d5db;
    }

    .footer-title {
      font-size: 10pt;
      font-weight: 700;
      margin-bottom: 5px;
      color: #1f2937;
    }

    .footer-content {
      font-size: 9pt;
      color: #4b5563;
      white-space: pre-wrap;
      line-height: 1.5;
    }

    .delivery-info {
      margin: 15px 0;
      padding: 12px;
      background: #fef3c7;
      border-left: 4px solid #f59e0b;
      border-radius: 4px;
    }

    .delivery-info-title {
      font-size: 10pt;
      font-weight: 700;
      margin-bottom: 8px;
      color: #92400e;
    }

    .delivery-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
      font-size: 9pt;
    }

    @media print {
      body {
        print-color-adjust: exact;
        -webkit-print-color-adjust: exact;
      }

      .container {
        max-width: 100%;
        padding: 0;
      }

      .no-print {
        display: none !important;
      }

      @page {
        margin: 15mm;
      }
    }
  </style>
`;
